import { useRef, useMemo, useEffect, useImperativeHandle, forwardRef } from 'react';
import type { ComponentPropsWithRef } from 'react';
import { Select, InputGroup, InputField } from '@kiwicom/orbit-components';
import type { TranslationType } from '@kiwicom/account-consts';
import { getDatefnsLocale } from '@kiwicom/account-client-utils';
import { useIntl } from 'react-intl';
import { padStartZero } from '@kiwicom/account-client-utils';
import type { FieldError, Merge, FieldErrorsImpl } from 'react-hook-form';
import type { Month } from 'date-fns';

export type DateType = {
  readonly day: string;
  readonly month: string;
  readonly year: string;
};
type Props = {
  readonly day: string | null | undefined;
  readonly month: string | null | undefined;
  readonly year: string | null | undefined;
  readonly label: TranslationType;
  readonly onChange: (arg0: DateType) => void;
  readonly dataTest?: string;
  readonly errorDataTest?: string;
  readonly disabled?: boolean;
  readonly error?: Merge<FieldError, FieldErrorsImpl<unknown>> | TranslationType | null | undefined;
  readonly onBlur?: ComponentPropsWithRef<'input'>['onBlur'];
  readonly flex?: string | string[];
};
type DateSelectRef = {
  readonly focus: () => void;
};

const SimpleDateSelect = (
  {
    label,
    day,
    month,
    year,
    onChange,
    disabled = false,
    error,
    onBlur,
    dataTest,
    errorDataTest,
    flex,
  }: Props,
  ref,
): JSX.Element => {
  const intl = useIntl();
  const { locale } = intl;
  const currentLocale = getDatefnsLocale(locale);
  const dayRef = useRef(null);
  const yearRef = useRef(null);
  const localizeMonth = currentLocale?.localize?.month;
  const months = useMemo(() => {
    const localizedMonths = [];

    if (localizeMonth != null) {
      for (let i = 0; i < 12; i++) {
        localizedMonths.push({
          label: localizeMonth(i as Month),
          value: padStartZero(i + 1),
        });
      }
    }

    return localizedMonths;
  }, [localizeMonth]);

  function onInputGroupChange(e) {
    const { name, value } = e.target;
    const newDate = {
      day: day ?? '',
      month: month ?? '',
      year: year ?? '',
      [name]: value,
    };
    onChange(newDate);
  }

  useEffect(() => {
    const dayElement = dayRef.current;
    const yearElement = yearRef.current;

    if (dayElement != null) {
      dayElement.setAttribute(
        'aria-label',
        intl.formatMessage({
          id: 'account_v2.components.simple_date_select.day_aria_label',
          defaultMessage: 'Day of birth, max length 2',
          description: 'This is for screen readers only',
        }),
      );
    }

    if (yearElement != null) {
      yearElement.setAttribute(
        'aria-label',
        intl.formatMessage({
          id: 'account_v2.components.simple_date_select.year_aria_label',
          defaultMessage: 'Year of birth, max length 4',
          description: 'This is for screen readers only',
        }),
      );
    }
  }, []);

  const focus = () => {
    if (dayRef.current != null) {
      dayRef.current.focus();
    }
  };

  const handleBlur = (e) => {
    onBlur(e);
  };

  useImperativeHandle(ref, () => ({
    focus,
  }));

  return (
    <InputGroup
      flex={flex ?? ['2', '6', '3']}
      onChange={onInputGroupChange}
      label={label}
      // @ts-expect-error: react-hook-form returns FieldError which is then resolved to ReactNode based on form schema
      error={error && <span data-test={errorDataTest}>{error}</span>}
      onBlur={handleBlur}
      dataTest={dataTest}
    >
      <InputField
        maxLength={2}
        disabled={disabled}
        placeholder={
          intl.formatMessage({
            id: 'account_v2.components.simple_date_select.day',
            defaultMessage: 'DD',
          }) ?? ''
        }
        name="day"
        value={day ?? ''}
        dataTest="day"
        ref={dayRef}
        inputMode="numeric"
      />
      <Select
        placeholder={
          intl.formatMessage({
            id: 'account_v2.components.simple_date_select.select_month',
            defaultMessage: 'Select month',
          }) ?? ''
        }
        name="month"
        options={months}
        value={typeof month === 'string' ? padStartZero(month) : ''}
        dataTest="month"
        disabled={disabled}
      />
      <InputField
        disabled={disabled}
        placeholder={
          intl.formatMessage({
            id: 'account_v2.components.simple_date_select.year',
            defaultMessage: 'YYYY',
          }) ?? ''
        }
        name="year"
        value={year ?? ''}
        dataTest="year"
        ref={yearRef}
        maxLength={4}
        inputMode="numeric"
      />
    </InputGroup>
  );
};

export default forwardRef<DateSelectRef, Props>(SimpleDateSelect);
